import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

export default function Materials() {
  const { projectId, canWrite } = useProjectCtx();
  const canEdit = canWrite("PROCUREMENT");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/materials`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  const leafItems = items.filter((i) => !i.is_group);

  return (
    <div className="space-y-4" data-testid="materials-page">
      <PageHeader title="Materials — Procurement Status" description={canEdit ? "You can update PO / MFC / delivery status." : "Read-only for your project role."} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && leafItems.length === 0 ? (
          <EmptyState title="No procurement items" testId="materials-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Material</TableHead><TableHead>Supplier</TableHead><TableHead>Planned delivery</TableHead><TableHead>Actual delivery</TableHead><TableHead>Status</TableHead><TableHead>Received</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {leafItems.map((i) => (
                <TableRow key={i.id} data-testid={`material-row-${i.id}`}>
                  <TableCell className="font-mono text-xs">{i.code}</TableCell>
                  <TableCell className="text-foreground">{i.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{i.material?.supplier || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.material?.planned_delivery_date)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.material?.actual_delivery_date)}</TableCell>
                  <TableCell><StatusPill value={i.material?.current_status} /></TableCell>
                  <TableCell className="font-mono text-xs">{fmtNum(i.material?.qty_received)} / {fmtNum(i.material?.qty_ordered)}</TableCell>
                  <TableCell className="text-right">
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`edit-material-btn-${i.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {editing && <MaterialDialog projectId={projectId} item={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function MaterialDialog({ projectId, item, onClose, onSaved }) {
  const d = item.material || {};
  const [form, setForm] = useState({
    priority: d.priority || "", qty_rfq: d.qty_rfq ?? "", qty_ordered: d.qty_ordered ?? "", qty_received: d.qty_received ?? "",
    rfq_date: d.rfq_date || "", planned_order_date: d.planned_order_date || "", planned_mfc_date: d.planned_mfc_date || "", planned_delivery_date: d.planned_delivery_date || "",
    loi_date: d.loi_date || "", order_no: d.order_no || "", actual_order_date: d.actual_order_date || "", actual_mfc_date: d.actual_mfc_date || "",
    unpriced_po_date: d.unpriced_po_date || "", outlook_delivery_date: d.outlook_delivery_date || "", lead_time_days: d.lead_time_days ?? "",
    current_status: d.current_status || "", supplier: d.supplier || "", inspection_date: d.inspection_date || "", mdcc_date: d.mdcc_date || "",
    dispatch_date: d.dispatch_date || "", actual_delivery_date: d.actual_delivery_date || "", remarks: d.remarks || "", entry_date: todayISO(),
  });
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const dateFields = ["rfq_date", "planned_order_date", "planned_mfc_date", "planned_delivery_date", "loi_date", "actual_order_date", "actual_mfc_date", "unpriced_po_date", "outlook_delivery_date", "inspection_date", "mdcc_date", "dispatch_date", "actual_delivery_date"];
  const numFields = ["qty_rfq", "qty_ordered", "qty_received", "lead_time_days"];

  const submit = async () => {
    try {
      const payload = { ...form };
      dateFields.forEach((f) => { payload[f] = payload[f] || null; });
      numFields.forEach((f) => { payload[f] = payload[f] === "" ? null : Number(payload[f]); });
      await api.patch(`/projects/${projectId}/materials/${item.id}`, payload);
      toast.success("Material status updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="material-dialog">
        <DialogHeader><DialogTitle>{item.code} — {item.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Priority</Label><Input data-testid="mat-priority-input" value={form.priority} onChange={set("priority")} className="mt-1.5" placeholder="Long Lead / Critical" /></div>
          <div><Label>Supplier</Label><Input data-testid="mat-supplier-input" value={form.supplier} onChange={set("supplier")} className="mt-1.5" /></div>
          <div><Label>Qty RFQ'd</Label><Input type="number" data-testid="mat-qty-rfq-input" value={form.qty_rfq} onChange={set("qty_rfq")} className="mt-1.5" /></div>
          <div><Label>Qty ordered</Label><Input type="number" data-testid="mat-qty-ordered-input" value={form.qty_ordered} onChange={set("qty_ordered")} className="mt-1.5" /></div>
          <div><Label>Qty received</Label><Input type="number" data-testid="mat-qty-received-input" value={form.qty_received} onChange={set("qty_received")} className="mt-1.5" /></div>
          <div><Label>Lead time (days)</Label><Input type="number" data-testid="mat-lead-time-input" value={form.lead_time_days} onChange={set("lead_time_days")} className="mt-1.5" /></div>

          <p className="col-span-2 mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Planned (L4)</p>
          <div><Label>RFQ date</Label><Input type="date" data-testid="mat-rfq-date-input" value={form.rfq_date} onChange={set("rfq_date")} className="mt-1.5" /></div>
          <div><Label>Planned order date</Label><Input type="date" data-testid="mat-planned-order-date-input" value={form.planned_order_date} onChange={set("planned_order_date")} className="mt-1.5" /></div>
          <div><Label>Planned MFC date</Label><Input type="date" data-testid="mat-planned-mfc-date-input" value={form.planned_mfc_date} onChange={set("planned_mfc_date")} className="mt-1.5" /></div>
          <div><Label>Planned delivery date</Label><Input type="date" data-testid="mat-planned-delivery-date-input" value={form.planned_delivery_date} onChange={set("planned_delivery_date")} className="mt-1.5" /></div>

          <p className="col-span-2 mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actual status</p>
          <div><Label>LOI date</Label><Input type="date" data-testid="mat-loi-date-input" value={form.loi_date} onChange={set("loi_date")} className="mt-1.5" /></div>
          <div><Label>Order no.</Label><Input data-testid="mat-order-no-input" value={form.order_no} onChange={set("order_no")} className="mt-1.5" /></div>
          <div><Label>Actual order date</Label><Input type="date" data-testid="mat-actual-order-date-input" value={form.actual_order_date} onChange={set("actual_order_date")} className="mt-1.5" /></div>
          <div><Label>Actual MFC date</Label><Input type="date" data-testid="mat-actual-mfc-date-input" value={form.actual_mfc_date} onChange={set("actual_mfc_date")} className="mt-1.5" /></div>
          <div><Label>Unpriced PO date</Label><Input type="date" data-testid="mat-unpriced-po-date-input" value={form.unpriced_po_date} onChange={set("unpriced_po_date")} className="mt-1.5" /></div>
          <div><Label>Outlook delivery date</Label><Input type="date" data-testid="mat-outlook-delivery-date-input" value={form.outlook_delivery_date} onChange={set("outlook_delivery_date")} className="mt-1.5" /></div>
          <div><Label>Inspection date</Label><Input type="date" data-testid="mat-inspection-date-input" value={form.inspection_date} onChange={set("inspection_date")} className="mt-1.5" /></div>
          <div><Label>MDCC date</Label><Input type="date" data-testid="mat-mdcc-date-input" value={form.mdcc_date} onChange={set("mdcc_date")} className="mt-1.5" /></div>
          <div><Label>Dispatch date</Label><Input type="date" data-testid="mat-dispatch-date-input" value={form.dispatch_date} onChange={set("dispatch_date")} className="mt-1.5" /></div>
          <div><Label>Actual delivery date</Label><Input type="date" data-testid="mat-actual-delivery-date-input" value={form.actual_delivery_date} onChange={set("actual_delivery_date")} className="mt-1.5" /></div>
          <div><Label>Current status</Label><Input data-testid="mat-current-status-input" value={form.current_status} onChange={set("current_status")} className="mt-1.5" /></div>
          <div className="col-span-2"><Label>Remarks</Label><Textarea data-testid="mat-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <div><Label>Back-date entry</Label><Input type="date" data-testid="mat-entry-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="material-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
